import 'react-virtualized/styles.css';
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { tableWith } from 'tuc-webui-base/components/TableViewComponent';
import DataColumn from './DataColumn';
import EditColumn from './EditColumn';
import CheckColumn from './CheckColumn';
import RowRenderer from './RowRenderer';
import './VirtualizedTable.scss';

const DENSE_SIZE = 'dense';
class MuiVirtualizedTable extends React.PureComponent {
  static getDerivedStateFromProps(props, state = {}) {
    const newState = {};
    let updated = false;
    const { canCheck, canEdit, canDelete, rowButtons, headerButtons } = props;
    if (state.refConfig !== props.configuration) {
      const { configuration } = props;
      const dense = configuration.size === DENSE_SIZE;
      newState.dense = dense;
      newState.rowHeight = configuration.rowHeight || (dense ? 48 : 53);
      newState.headerHeight = configuration.headerHeight || (dense ? 57 : 70);
      updated = true;
    }
    if ((state.refColumns !== props.columns)
        || (state.refCanCheck !== canCheck)) {
      newState.refColumns = props.columns;
      newState.refCanCheck = canCheck;
      const columns = (canCheck ? [CheckColumn(props)] : [])
        .concat(props.columns.map((col, i) => DataColumn(col, i, props)))
        .concat((canEdit || canDelete || rowButtons || headerButtons)
          ? [EditColumn(props.editColumn, props.columns.length, props)] : []);
      newState.columns = columns;
      updated = true;
    }
    return updated ? newState : null;
  }

  constructor(props) {
    super(props);

    // Bindings
    this.getRow = this.getRow.bind(this);
    this.getCellData = this.getCellData.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
    this.rowClassName = this.rowClassName.bind(this);

    // Methods for creating Virtualized columns
    const { configuration } = props;
    const tableHeight = configuration && configuration.tableHeight && `${configuration.tableHeight}px` || undefined;
    this.ctnrStyle = tableHeight ? { height: tableHeight } : { flex: '1 1 auto' };
    if (configuration && configuration.tableWidth) {
      this.ctnrStyle.width = `${configuration.tableWidth}px`;
    }
    this.ctnrTableClassname = classnames('grb-virutalized-table-ctnr', configuration && configuration.ctnrClassName);

    const { getTableRowId } = this.props.tableHandler;
    this.RowRenderer = getTableRowId ? RowRenderer({ getTableRowId }) : undefined;

    this.state = {
      cellProperties: {},
    };
  }

  async componentDidMount() {
    const {
      AutoSizer, Column, Table, InfiniteLoader,
    } = await import(/* webpackChunkName: "react-virtualized" */ 'react-virtualized');
    this.setState({ AutoSizer, Column, Table, InfiniteLoader });
  }

  getRow({ index }) {
    return this.props.rowNodes[index];
  }

  getCellData({ dataKey, rowData }) {
    return rowData.fvalues[dataKey];
  }

  rowClassName({ index }) {
    if (index < 0) {
      return 'grb-virtualized-table-header-row';
    }
    const node = this.props.rowNodes[index];
    return node && node.selected ? 'grb-virtualized-table-row Mui-selected' : 'grb-virtualized-table-row';
  }

  handleRowClick({ event, rowData }) {
    if (rowData) {
      this.props.tableHandler.onSelectClick(event, rowData.obj);
    }
  }

  render() {
    const { AutoSizer, Column, Table, InfiniteLoader, columns, rowHeight, headerHeight, dense } = this.state;
    if (!AutoSizer) {
      return <div />;
    }
    // checked is used to force a redraw of the table is the user clicks the 'check all' button
    // of the table.
    const { rowNodes, scrollToSelection, selected, configuration } = this.props;
    let scrollToIndex;
    if (scrollToSelection && selected && selected.length) {
      scrollToIndex = rowNodes.indexOf(selected[0]);
      if (scrollToIndex < 0) scrollToIndex = undefined;
    }
    const { tableHandler } = this.props;

    const paging = tableHandler.isPaging();
    const rowCount = paging ? tableHandler.getPagingRowCount() : rowNodes.length;
    let msg = '';
    const minWidth = columns.reduce((total, col) => {
      msg = `${msg} ${col.name}=${col.minWidth(this.props)}`;
      return total + col.minWidth(this.props) + 10; // right margin for each cell
    }, 10); // left margin for first cell

    const renderTable = ({ onRowsRendered, registerChild }) => (
      <AutoSizer>
        {({ height, width }) => {
          const tableProps = { ...this.props, width };
          return (
            <Table
              ref={registerChild}
              onRowsRendered={onRowsRendered}
              rowCount={rowCount}
              rowGetter={this.getRow}
              height={height}
              width={width}
              rowHeight={rowHeight}
              gridStyle={{
                direction: 'inherit',
              }}
              scrollToIndex={scrollToIndex}
              headerHeight={headerHeight}
              className={classnames(
                'grb-virtualized-table',
                configuration && configuration.className,
                dense && 'grb-virtualized-table-dense',
                this.props.treeTable && 'tuc-table-view-tree',
                this.state.refCanCheck && 'grb-virtualized-table-checkable',
              )}
              rowClassName={this.rowClassName}
              onRowClick={this.handleRowClick}
              rowRenderer={this.RowRenderer}
            >
              {columns.map(({ generateProps }, index) => (
                <Column {...generateProps(tableProps)} dataKey={index} />
              ))}
            </Table>
          );
        }}
      </AutoSizer>
    );
    // if (paging) {
      // const printHoles = (nodes, start = 0) => {
      //   let i;
      //   for (i = start; i < nodes.length; i += 1) {
      //     if (!nodes[i]) {
      //       break;
      //     }
      //   }
      //   if (i < nodes.length) {
      //     let e = i + 1;
      //     while ((e < nodes.length) && !nodes[e]) {
      //       e += 1;
      //     }
      //     return `Hole (${i} - ${e - 1}) ${printHoles(nodes, e)}`;
      //   }
      //   return '';
      // };
      // console.log('Render Virtualized table ', printHoles(rowNodes));
    // }
    return (
      <div
        className={this.ctnrTableClassname}
        style={this.ctnrStyle}
      >
        <div className="grb-virtualized-table-content" style={{ minWidth }}>
          {paging ? (
            <InfiniteLoader
              isRowLoaded={tableHandler.isRowLoaded}
              loadMoreRows={tableHandler.onLoadPage}
              rowCount={rowCount}
              minimumBatchSize={40}
            >
              {renderTable}
            </InfiniteLoader>
          ) : renderTable({})}
        </div>
      </div>
    );
  }
}

MuiVirtualizedTable.propTypes = {
  rowNodes: PropTypes.arrayOf(PropTypes.shape({
    selected: PropTypes.bool,
  })).isRequired,
  selected: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  checked: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      numeric: PropTypes.bool,
      width: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.number),
      ]),
    }),
  ).isRequired,
  editColumn: PropTypes.shape({}),
  editedRow: PropTypes.shape({}),
  editValues: PropTypes.arrayOf(PropTypes.any).isRequired,
  validationErrors: PropTypes.arrayOf(PropTypes.string),
  onRowClick: PropTypes.func,
  readOnly: PropTypes.bool,
  canCreate: PropTypes.bool,
  canDelete: PropTypes.bool,
  canEdit: PropTypes.bool,
  showActionButtons: PropTypes.bool,
  canCheck: PropTypes.bool,
  treeTable: PropTypes.bool,
  tableDepth: PropTypes.number,
  cellProperties: PropTypes.shape({}),
  headerButtons: PropTypes.func,
  rowButtons: PropTypes.func,
  tableHandler: PropTypes.shape({
    getString: PropTypes.func,
    getIcon: PropTypes.func,
    onCheckAllClick: PropTypes.func,
    onCheckClick: PropTypes.func,
    onSelectClick: PropTypes.func,
    onEditCell: PropTypes.func,
    onSort: PropTypes.func,
    onStopEditing: PropTypes.func,
    onStartEditing: PropTypes.func,
    onCancelEditing: PropTypes.func,
    onCreate: PropTypes.func,
    onDelete: PropTypes.func,
    getTableRowId: PropTypes.func,
    onLoadPage: PropTypes.func,
    isLoaded: PropTypes.func,
    isPaging: PropTypes.func,
    getPagingRowCount: PropTypes.func,
    isRowLoaded: PropTypes.func,
  }).isRequired,
  scrollToSelection: PropTypes.bool,
  sortedColumn: PropTypes.shape({}),
  sortOrder: PropTypes.string,
  configuration: PropTypes.shape({
    size: PropTypes.string,
    tableHeight: PropTypes.number,
    tableWidth: PropTypes.number,
    rowHeight: PropTypes.number,
    headerHeight: PropTypes.number,
    add: PropTypes.shape({
      icon: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({}),
      ]),
      tooltip: PropTypes.string,
    }),
    delete: PropTypes.shape({
      icon: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({}),
      ]),
      tooltip: PropTypes.string,
    }),
    ctnrClassName: PropTypes.string,
    className: PropTypes.string,
  }),
};

MuiVirtualizedTable.defaultProps = {
  onRowClick: undefined,
  editColumn: undefined,
  editedRow: undefined,
  validationErrors: undefined,
  canCreate: undefined,
  canDelete: undefined,
  canEdit: undefined,
  showActionButtons: undefined,
  canCheck: undefined,
  readOnly: false,
  treeTable: false,
  tableDepth: 0,
  cellProperties: undefined,
  scrollToSelection: undefined,
  sortedColumn: undefined,
  sortOrder: undefined,
  configuration: undefined,
  rowButtons: undefined,
  headerButtons: undefined,
};

export default tableWith(MuiVirtualizedTable, {
  getScrollElement: () => {
    // const grid = tableElt.getElementsByClassName('ReactVirtualized__Table__Grid');
    // return grid && grid.length ? grid[0] : null;
    return null;
  },
  autoScrollToSelection: false,
  getTableImplProps: (table) => {
    if (table.scroll.scrollToSelection) {
      return { scrollToSelection: true };
    }
    return null;
  },
});

